import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { RotateCw, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const MAX_SIDE = 1600;

const loadImage = (url) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });

async function cropToBlob(url, area, rotation) {
  const img = await loadImage(url);
  const rad = (rotation * Math.PI) / 180;
  const rotW = Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.height);
  const rotH = Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height);
  const stage = document.createElement("canvas");
  stage.width = rotW; stage.height = rotH;
  const sc = stage.getContext("2d");
  sc.translate(rotW / 2, rotH / 2); sc.rotate(rad); sc.drawImage(img, -img.width / 2, -img.height / 2);
  const scale = Math.min(1, MAX_SIDE / Math.max(area.width, area.height));
  const out = document.createElement("canvas");
  out.width = Math.round(area.width * scale); out.height = Math.round(area.height * scale);
  out.getContext("2d").drawImage(stage, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height);
  return new Promise((res) => out.toBlob(res, "image/jpeg", 0.85));
}

export const ReceiptCropDialog = ({ file, onDone, onCancel }) => {
  const [url, setUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setUrl(u); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const finish = async () => {
    setBusy(true);
    try {
      const blob = await cropToBlob(url, area, rotation);
      onDone(new File([blob], (file.name || "receipt").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="receipt-crop-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-heading">Crop receipt</DialogTitle>
          <DialogDescription>Drag to position, pinch or use the slider to zoom, rotate if it's sideways.</DialogDescription>
        </DialogHeader>
        <div className="relative h-[55vh] bg-black" data-testid="receipt-crop-area">
          {url && <Cropper image={url} crop={crop} zoom={zoom} rotation={rotation} onCropChange={setCrop} onZoomChange={setZoom}
            onCropComplete={(_, px) => setArea(px)} aspect={3 / 4} showGrid={false} objectFit="contain" restrictPosition={false} minZoom={0.5} maxZoom={4} />}
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <input data-testid="receipt-crop-zoom" type="range" min="0.5" max="4" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-emerald-600" aria-label="Zoom" />
            <button type="button" data-testid="receipt-crop-rotate" onClick={() => setRotation((r) => (r + 90) % 360)} className="h-10 w-10 grid place-items-center rounded-lg border border-border hover:bg-secondary" aria-label="Rotate 90°"><RotateCw className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy} data-testid="receipt-crop-cancel"><X className="h-4 w-4 mr-2" /> Cancel</Button>
            <Button type="button" onClick={finish} disabled={busy || !area} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="receipt-crop-done"><Check className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Use photo"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
